import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Briefcase, MapPin, Clock, ShieldCheck, CheckCircle2, 
  FileUp, Loader2, User, Mail, Phone, Building2, Send, MessageCircle, AlertCircle
} from 'lucide-react';
import SEO from '../seo/SEO';
import { db } from '../firebase';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sendNotificationEmail } from '../utils/emailService';

const JobDetailPage = () => {
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [job, setJob] = useState(location.state?.job || null);
  const [loadingJob, setLoadingJob] = useState(!location.state?.job);
  const [formStatus, setFormStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Fetch job if not passed via router state
  useEffect(() => {
    if (!job && jobId) {
      const loadJob = async () => {
        setLoadingJob(true);
        try {
          const docRef = doc(db, 'jobs', jobId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setJob({ id: docSnap.id, ...docSnap.data() });
          } else {
            setJob(null);
          }
        } catch (error) {
          console.error("Error loading job details from Firebase:", error);
          setJob(null);
        } finally {
          setLoadingJob(false);
        }
      };
      loadJob();
    }
  }, [jobId, job]);

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    setFormStatus('loading');
    setErrorMessage('');

    const formData = new FormData(e.target);
    const applicantName = formData.get('name');
    const applicantEmail = formData.get('email');
    const applicantPhone = formData.get('phone');
    const applicantExp = formData.get('experience') || 'Not specified';
    const applicantNote = formData.get('note') || '';
    const resumeFile = formData.get('resume');

    try {
      let base64Resume = '';
      if (resumeFile && resumeFile.size > 0) {
        if (resumeFile.size > 5 * 1024 * 1024) {
          throw new Error('Resume file size must be less than 5MB.');
        }
        base64Resume = await toBase64(resumeFile);
      }

      // 1. Store application directly in Firebase DB
      await addDoc(collection(db, 'applications'), {
        name: applicantName,
        email: applicantEmail,
        number: applicantPhone,
        experience: applicantExp,
        note: applicantNote,
        jobId: job?.id || jobId,
        jobTitle: job?.title || 'General Applicant',
        jobCategory: job?.category || 'General',
        roleType: job?.type || 'General',
        resume: base64Resume,
        resumeFileName: resumeFile?.name || 'resume.pdf',
        status: 'New',
        createdAt: serverTimestamp(),
        createdAtString: new Date().toLocaleString()
      });

      // 2. Dispatch notification email to both addresses
      await sendNotificationEmail({
        formType: `Job Application (${job?.type || 'General'})`,
        subject: `[Job Application] ${applicantName} applied for ${job?.title || 'Open Role'}`,
        data: {
          Candidate_Name: applicantName,
          Candidate_Email: applicantEmail,
          Candidate_Phone: applicantPhone,
          Experience: applicantExp,
          Applied_Role: job?.title || 'N/A',
          Role_Category: job?.category || 'N/A',
          Role_Type: job?.type || 'N/A',
          Candidate_Note: applicantNote || 'None'
        }
      });

      setFormStatus('success');
      // Scroll to application card
      const formCard = document.getElementById('apply-section');
      if (formCard) {
        formCard.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      setErrorMessage(err.message || 'Failed to submit application. Please try again.');
      setFormStatus('error');
    }
  };

  const backLink = job?.type?.toUpperCase() === 'IT' ? '/it-careers' : '/non-it-careers';
  const backLabel = job?.type?.toUpperCase() === 'IT' ? 'Back to IT Careers' : 'Back to Non-IT Careers';

  if (loadingJob) {
    return (
      <div className="min-h-screen bg-slate-50 pt-40 pb-20 flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 text-amber-500 animate-spin mb-4" />
        <p className="text-slate-600 font-medium">Loading position details...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50 pt-40 pb-20 container mx-auto px-4 text-center">
        <h2 className="text-3xl font-bold text-slate-900 mb-4">Position Not Found</h2>
        <p className="text-slate-600 mb-8">The role you are looking for is no longer active or has been updated.</p>
        <Link to="/it-careers" className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-bold">
          <ArrowLeft className="h-4 w-4" /> View All Openings
        </Link>
      </div>
    );
  }

  // Parse skills into array
  const skillsList = job.skills
    ? job.skills.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  return (
    <div className="min-h-screen bg-slate-50 pt-32 pb-24 text-slate-900">
      <SEO
        title={`${job.title} | Career Opening at GlobalEPRC`}
        description={`Explore job details and apply for ${job.title} (${job.category || 'Career'}) with GlobalEPRC. Ethical recruitment, transparent contracts, and verified openings.`}
        path={`/careers/${job.id || jobId}`}
      />

      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        {/* Navigation Bar */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(backLink)}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-amber-600 transition-colors bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 bg-amber-100 text-amber-800 rounded-full">
              {job.type || 'Open Role'}
            </span>
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 bg-slate-200 text-slate-800 rounded-full">
              {job.category || 'Specialized'}
            </span>
          </div>
        </div>

        {/* Hero Card */}
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl border border-slate-100 mb-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-amber-100/40 via-yellow-50/20 to-transparent rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-600 mb-4">
              <Building2 className="h-4 w-4" /> GlobalEPRC Talent Resourcing
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
              {job.title}
            </h1>

            {/* Quick Meta Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-6 border-y border-slate-100 my-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                  <Briefcase className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-medium">Job Type</div>
                  <div className="text-sm font-bold text-slate-800">Full-Time / Direct</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-medium">Location</div>
                  <div className="text-sm font-bold text-slate-800">India / Overseas</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-medium">Placement</div>
                  <div className="text-sm font-bold text-slate-800">Immediate / Active</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-medium">Integrity</div>
                  <div className="text-sm font-bold text-slate-800">Ethical Sourced</div>
                </div>
              </div>
            </div>

            {/* Quick action CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className="text-slate-600 text-sm max-w-xl">
                Ready to take your next career step? Review the complete role requirements below and submit your profile directly to our hiring team.
              </p>
              <button
                onClick={() => {
                  document.getElementById('apply-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-black text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                Apply for this Position →
              </button>
            </div>
          </div>
        </div>

        {/* Content Layout: 2 Columns on desktop */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Job Details */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* Role Overview */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-2 h-6 bg-amber-500 rounded-full inline-block"></span>
                Role Overview
              </h2>
              <p className="text-slate-600 leading-relaxed text-base whitespace-pre-line">
                {job.desc}
              </p>
            </div>

            {/* Key Skills & Requirements */}
            {skillsList.length > 0 && (
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-2 h-6 bg-amber-500 rounded-full inline-block"></span>
                  Key Requirements & Skills
                </h2>
                <div className="flex flex-wrap gap-2.5 mb-6">
                  {skillsList.map((skill, idx) => (
                    <span 
                      key={idx}
                      className="px-4 py-2 bg-slate-100 text-slate-800 text-sm font-semibold rounded-xl border border-slate-200/60"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Applicants with matching certifications, trade licenses, or equivalent hands-on experience will receive priority shortlisting.
                </p>
              </div>
            )}

            {/* Ethical Hiring Assurance */}
            <div className="bg-gradient-to-br from-amber-50/80 to-yellow-50/40 border border-amber-200/60 rounded-3xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">The GlobalEPRC Candidate Promise</h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0" />
                      Strictly ethical recruitment compliant with Ministry & Labor norms.
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0" />
                      Direct interviews with verified employer partners (no hidden middle layers).
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0" />
                      End-to-end guidance for documentation, travel coordination, and onboarding.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* WhatsApp Text Assistance */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <MessageCircle className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs uppercase font-bold text-emerald-700">Have Questions About This Role?</div>
                  <div className="text-sm font-bold text-slate-800">WhatsApp Text Us Here: +91 8610452001</div>
                </div>
              </div>
              <a
                href="https://wa.me/918610452001?text=Hello%20GlobalEPRC,%20I%20have%20an%20enquiry%20regarding%20the%20job%20opening."
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl text-center transition"
              >
                Chat on WhatsApp
              </a>
            </div>

          </div>

          {/* Right Column: Apply Form Section */}
          <div id="apply-section" className="lg:col-span-5 sticky top-28">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-xl border border-slate-100">
              
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-50 text-green-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-10 w-10 animate-bounce" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-2">Application Submitted!</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    Thank you for applying for <span className="font-bold text-slate-900">{job.title}</span>. Our recruitment specialists have received your details and will get in touch shortly.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setFormStatus('idle');
                        setUploadedFileName('');
                      }}
                      className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-sm transition"
                    >
                      Submit Another Application
                    </button>
                    <Link
                      to={backLink}
                      className="block w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-sm text-center transition"
                    >
                      Browse More Openings
                    </Link>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Direct Application</span>
                    <h3 className="text-2xl font-black text-slate-900 mt-1">
                      Apply for this {job.type === 'Non-IT' ? 'Trade' : 'Role'}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Applying for: <span className="font-bold text-slate-700">{job.title}</span>
                    </p>
                  </div>

                  {errorMessage && (
                    <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <form onSubmit={handleApplySubmit} className="space-y-4">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          name="name"
                          required
                          type="text"
                          placeholder="Your Full Name"
                          className="w-full pl-11 pr-4 py-3.5 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-amber-500 focus:bg-white outline-none transition"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">
                        Work / Personal Email *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          name="email"
                          required
                          type="email"
                          placeholder="name@example.com"
                          className="w-full pl-11 pr-4 py-3.5 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-amber-500 focus:bg-white outline-none transition"
                        />
                      </div>
                    </div>

                    {/* Phone / WhatsApp */}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">
                        Phone / WhatsApp Number *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          name="phone"
                          required
                          type="tel"
                          placeholder="+91 98765 43210"
                          className="w-full pl-11 pr-4 py-3.5 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-amber-500 focus:bg-white outline-none transition"
                        />
                      </div>
                    </div>

                    {/* Experience Level */}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">
                        Total Experience
                      </label>
                      <select
                        name="experience"
                        className="w-full px-4 py-3.5 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-amber-500 focus:bg-white outline-none transition font-medium"
                      >
                        <option value="Fresher / Entry Level">Fresher / Entry Level (0 - 1 Year)</option>
                        <option value="1 - 3 Years">1 - 3 Years</option>
                        <option value="3 - 5 Years">3 - 5 Years</option>
                        <option value="5 - 8 Years">5 - 8 Years</option>
                        <option value="8+ Years Senior">8+ Years Senior Level</option>
                      </select>
                    </div>

                    {/* Resume / Qualification PDF */}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">
                        Resume / CV (PDF, Max 5MB) *
                      </label>
                      <div className="relative border-2 border-dashed border-slate-200 hover:border-amber-400 rounded-xl p-4 bg-slate-50 text-center transition">
                        <input
                          name="resume"
                          required
                          type="file"
                          accept=".pdf"
                          id="resume-file-input"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setUploadedFileName(e.target.files[0].name);
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <FileUp className="h-6 w-6 text-slate-400 mx-auto mb-1" />
                        <span className="text-xs font-semibold text-slate-700 block truncate">
                          {uploadedFileName ? uploadedFileName : 'Click or drop PDF resume here'}
                        </span>
                        <span className="text-[10px] text-slate-400">PDF files only</span>
                      </div>
                    </div>

                    {/* Optional Note */}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">
                        Additional Note (Optional)
                      </label>
                      <textarea
                        name="note"
                        rows={2}
                        placeholder="Current notice period, expected salary, or key certifications..."
                        className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-amber-500 focus:bg-white outline-none transition"
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full bg-gold-gradient py-4 rounded-xl font-black text-slate-950 text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    >
                      {formStatus === 'loading' ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Submitting Application...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Confirm & Apply Now
                        </>
                      )}
                    </button>

                    <p className="text-[11px] text-slate-400 text-center pt-2">
                      Your details are kept strictly confidential and shared only with verified hiring managers.
                    </p>
                  </form>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;

